/**
 * One audit's cell: a score, what it cost, and when.
 *
 * The empty state is the point of this component. A site that has never been
 * audited one way is the most interesting row on the desk, so the gap has to be
 * legible at a glance rather than being an absence the eye slides over - it is
 * drawn as a dashed placeholder that says "not audited", not as blank space.
 */
import { ExternalLink } from "lucide-react";
import { cn } from "@wa/ui";
import { fmtDate } from "../../ui";
import { auditBand, type AuditCell } from "../../../lib/siteAudits";

/** Band to colour. Three steps, matching the public report's own bands. */
const BAND_COLOUR: Record<ReturnType<typeof auditBand>, string> = {
  good: "var(--color-success, #16a34a)",
  mid: "var(--color-warning, #d97706)",
  bad: "var(--color-destructive, #dc2626)",
};

export function AuditScore({
  cell,
  emptyLabel,
  issuesLabel,
  runsLabel,
  reportLabel,
}: {
  cell: AuditCell | null;
  emptyLabel: string;
  issuesLabel: string;
  runsLabel: string;
  reportLabel: string;
}) {
  if (!cell) {
    return (
      <span className="crm-mut inline-flex items-center rounded-lg border border-dashed border-[var(--color-border)] px-2.5 py-1 text-[12px]">
        {emptyLabel}
      </span>
    );
  }

  const colour = BAND_COLOUR[auditBand(cell.score)];
  return (
    <span className="flex min-w-0 items-center gap-2.5">
      <span
        className="crm-tnum inline-flex h-8 min-w-[38px] items-center justify-center rounded-lg px-1.5 text-[13px] font-bold"
        style={{ background: `color-mix(in oklab, ${colour} 14%, transparent)`, color: colour }}
        title={`${cell.score}/100`}
      >
        {cell.score}
      </span>
      <span className="flex min-w-0 flex-col leading-tight">
        <span className="crm-trunc text-[12.5px]">
          {cell.issues} {issuesLabel}
          {cell.runs > 1 ? (
            <span className="crm-mut"> {"·"} {cell.runs} {runsLabel}</span>
          ) : null}
        </span>
        <span className="crm-mut crm-tnum text-[11.5px]">{fmtDate(cell.at)}</span>
      </span>
      {/* `relative z-[1]`: the row itself is clickable, and this link has to
          win the click rather than opening the row behind it. */}
      <a
        href={cell.reportUrl}
        target="_blank"
        rel="noreferrer"
        title={reportLabel}
        aria-label={reportLabel}
        className={cn(
          "relative z-[1] ml-auto inline-flex size-7 shrink-0 items-center justify-center rounded-md",
          "text-[var(--color-primary)] hover:bg-[color-mix(in_oklab,var(--color-primary)_10%,transparent)]",
        )}
      >
        <ExternalLink className="size-[15px]" strokeWidth={1.8} />
      </a>
    </span>
  );
}
